<script lang="ts">
import { defineComponent, reactive, computed } from 'vue'
import SectionHeadingForContentDetail from '@/components/heading/SectionHeadingForContentDetail.vue'
import LayoutContentsCardsCols from '@/components/contents/LayoutContentsCardsCols.vue'
import LayoutContentsCardsRows from '@/components/contents/LayoutContentsCardsRows.vue'
import PaginationDefault from '@/components/pagination/PaginationDefault.vue'
import stateInitializer from '@/models/stateInitializer'
import paginationModel from '@/models/pagination'

export default defineComponent({
  name: 'ThemeMain',

  components: {
    SectionHeadingForContentDetail,
    LayoutContentsCardsCols,
    LayoutContentsCardsRows,
    PaginationDefault,
  },

  setup() {
    interface State {
      layoutType: Maybe<'REPORT_ONLY' | 'ARTICLE_ONLY' | 'REPORT_AND_ARTICLE'>,
      contents: {
        items: Array<ReportItem | ArticleItem>,
      },
      pagination: Pagination,
    }

    const state: State = reactive({
      layoutType: null,
      contents: {
        items: [],
      },
      pagination: {
        page: 0,
        per: 0,
        totalCount: 0,
        totalPage: 0,
      },
    })

    stateInitializer(state, 'main')
    const { pagination } = paginationModel(state.pagination)

    const isHorizontalCardLayout = computed<boolean>(() => {
      return state.layoutType !== 'REPORT_ONLY'
    })

    const imageOptions = computed<{
      width: {
        pc: number,
        sp: number,
      },
      height: {
        pc: number,
        sp: number,
      },
    }>(() => {
      return isHorizontalCardLayout.value
        ? {
          width: {
            pc: 72,
            sp: 72,
          },
          height: {
            pc: 72,
            sp: 72,
          },
        }
        : {
          width: {
            pc: 197,
            sp: 137,
          },
          height: {
            pc: 103,
            sp: 72,
          },
        }
    })

    return {
      // data
      state,

      // computed
      pagination,
      isHorizontalCardLayout,
      imageOptions,
    }
  },
})
</script>

<template>
  <section
    v-if="state.contents.items.length"
    class="section">
    <div class="l-unit__themeContents">
      <div class="l-unit__themeContents--header">
        <SectionHeadingForContentDetail>
          <template #primary>
            記事一覧
          </template>
          <template
            v-if="state.pagination.totalCount"
            #secondary>
            ({{ state.pagination.totalCount }})
          </template>
        </SectionHeadingForContentDetail>
      <!-- /.l-unit__themeContents header --></div>

      <div class="l-unit__themeContents--description">
        <template v-if="isHorizontalCardLayout">
          <LayoutContentsCardsRows
            :items="state.contents.items"
            :horizontal="{ pc: true, sp: true }"
            :heading-options="{ maxLine: 3 }"
            :image-options="imageOptions" />
        </template>
        <template v-else>
          <LayoutContentsCardsCols
            :items="state.contents.items"
            :cols="{ pc: 3, sp: 1 }"
            :horizontal="{ pc: false, sp: true }"
            :heading-options="{ maxLine: 3 }"
            :image-options="imageOptions" />
        </template>
      <!-- /.l-unit__themeContents description --></div>

      <div class="l-unit__themeContents--action is-pagination">
        <PaginationDefault
          :page="pagination.page"
          :offset="4"
          :total-page="pagination.totalPage || 0" />
      <!-- /.l-unit__themeContents action --></div>
    <!-- /.l-unit__themeContents --></div>
  </section>
</template>

<style lang="sass" scoped>
// layout
.l-unit__themeContents
  $root: &

  & &--header
    margin-bottom: 0.8em

  & &--description
    margin-top: 1em

  & &--action
    &.is-pagination
      margin-top: 1.5em
      padding-top: 1em
      border-top: 1px solid $border-x-light-gray

  +breakpoint
    #{$root}--action
      &.is-pagination
        margin-top: changeSizePx2Em(12)
        padding-top: changeSizePx2Em(12)
</style>
